"use client";

import { Bar } from "@visx/shape";
import { motion } from "framer-motion";
import React, { useId } from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";

interface BarSeriesProps {
  data: BaseDataPoint[];
  color?: string;
  label?: string;
  opacity?: number;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
}

/**
 * BarSeries
 *
 * Chart component for rendering bar/column charts.
 * Displays data as vertical bars with customizable colors, opacity, and category-based coloring.
 */
export const BarSeries: React.FC<BarSeriesProps> = ({
  data,
  color = "var(--color-viz-default)",
  opacity = 1,
  categoryColors = {},
}) => {
  const { xScale, yScale, animationSettings } = useChartContext();
  const clipPathId = useId();

  // Get the exact plotting area bounds from the scales
  const xRange = xScale.range();
  const yRange = yScale.range();

  // Sort data points by time
  const sortedData = [...data].sort((a, b) => a.xValue.getTime() - b.xValue.getTime());

  // Early return if no data (e.g., after zoom filtering)
  if (sortedData.length === 0) {
    return null;
  }

  // Calculate the time interval between points
  const timeInterval =
    sortedData.length > 1 ? sortedData[1].xValue.getTime() - sortedData[0].xValue.getTime() : 15 * 60000; // Default to 15 minutes if only one point

  // Calculate bar width based on the time interval
  const barWidth = Math.abs(
    xScale(new Date(sortedData[0].xValue.getTime() + timeInterval)) - xScale(sortedData[0].xValue)
  );

  return (
    <>
      <defs>
        <clipPath id={clipPathId}>
          <rect x={xRange[0]} y={yRange[1]} width={xRange[1] - xRange[0]} height={yRange[0] - yRange[1]} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipPathId})`}>
        <motion.g
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{
            duration: animationSettings.duration,
            ease: animationSettings.ease as "easeInOut",
          }}
          style={{
            transformOrigin: `0 ${yScale(0)}px`,
            isolation: "isolate",
          }}
        >
          {sortedData.map((d, i) => {
            const x = xScale(d.xValue);
            const y = yScale(d.yValue);
            const zeroY = yScale(0);

            // Bars extend from the zero baseline to the data value
            // Positive values: bar goes upward from zero (y < zeroY in SVG coords)
            // Negative values: bar goes downward from zero (y > zeroY in SVG coords)
            const barY = Math.min(y, zeroY);
            const barHeight = Math.abs(y - zeroY);

            // Calculate this bar's width based on the next point
            const nextPoint = sortedData[i + 1];
            const thisBarWidth = nextPoint ? Math.abs(xScale(nextPoint.xValue) - x) : barWidth;

            // Get color based on category if available
            const barColor = d.category && categoryColors[d.category] ? categoryColors[d.category] : color;

            return (
              <Bar
                key={d.xValue.getTime()}
                x={x - thisBarWidth / 2}
                y={barY}
                width={thisBarWidth}
                height={barHeight}
                fill={barColor}
                opacity={opacity}
                rx={1}
              />
            );
          })}
        </motion.g>
      </g>
    </>
  );
};
